import React, { useState } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useUserStatus } from '../../contexts/UserStatusContext';

const CheckoutForm = ({ plan, onClose, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { setStatus, refreshUserStatus } = useUserStatus();
  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Identify intent type from client secret
  const isSetupIntent = !!clientSecret && clientSecret.startsWith('seti_');
  const isPaymentIntent = !!clientSecret && clientSecret.startsWith('pi_');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setMessage(null);

    let result;
    if (isPaymentIntent) {
      // Payment intent flow (subscription first payment OR one-time payment)
      console.log('🔄 Confirming payment intent...');
      result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/payment-success',
        },
        redirect: 'if_required',
      });
    } else if (isSetupIntent) {
      // Setup intent flow (saving payment method without charging)
      console.log('🔄 Confirming setup intent...');
      result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/payment-success',
        },
        redirect: 'if_required',
      });
    } else {
      console.error('❌ Unknown client secret format:', clientSecret);
      setMessage('❌ Invalid payment configuration');
      setIsProcessing(false);
      return;
    }

    const { error, paymentIntent, setupIntent } = result || {};

    if (error) {
      setMessage(error.message);
    } else if (paymentIntent || setupIntent) {
      const status = paymentIntent?.status || setupIntent?.status;
      switch (status) {
        case 'succeeded':
          if (paymentIntent) {
            // Payment succeeded - card was charged!
            console.log('✅ Payment succeeded! Card was charged:', paymentIntent.id);
            setMessage('✅ Payment succeeded! Activating subscription...');
            
            // Poll for subscription activation (webhooks will update user status)
            const pollForActivation = async () => {
              try {
                await refreshUserStatus();
                console.log('🔄 Checking if subscription is active...');
                
                const currentStatus = localStorage.getItem('userStatus');
                if (currentStatus === 'pro') {
                  setMessage('✅ Subscription activated! Welcome to Pro! 🎉');
                  setTimeout(() => {
                    if (onClose) onClose();
                  }, 2000);
                } else {
                  // Keep polling for up to 20 seconds
                  setTimeout(pollForActivation, 2000);
                }
              } catch (error) {
                console.warn('⚠️ Error checking activation:', error);
                setMessage('⚠️ Subscription activating... Please refresh if needed.');
                setTimeout(() => {
                  if (onClose) onClose();
                }, 5000);
              }
            };
            
            // Start polling after a short delay for webhooks to process
            setTimeout(pollForActivation, 2000);
          } else if (setupIntent) {
            // Setup intent succeeded - payment method saved but not charged
            console.log('✅ Setup intent succeeded - payment method saved');
            setMessage('✅ Payment method saved! Processing subscription...');
            
            // Poll for subscription status change
            const pollForSubscription = async () => {
              try {
                await refreshUserStatus();
                console.log('🔄 Checking subscription status...');
                
                const currentStatus = localStorage.getItem('userStatus');
                if (currentStatus === 'pro') {
                  setMessage('✅ Subscription activated! Welcome to Pro! 🎉');
                  setTimeout(() => {
                    if (onClose) onClose();
                  }, 2000);
                } else {
                  setTimeout(pollForSubscription, 2000);
                }
              } catch (error) {
                console.warn('⚠️ Error polling subscription:', error);
                setMessage('⚠️ Subscription processing... Please refresh the page.');
                setTimeout(() => {
                  if (onClose) onClose();
                }, 5000);
              }
            };
            
            setTimeout(pollForSubscription, 3000);
          }
          break;
        case 'requires_action':
          setMessage('⏳ Please complete additional verification.');
          break;
        case 'processing':
          setMessage('⏳ Your payment is processing.');
          break;
        case 'requires_payment_method':
          setMessage('❌ Payment failed. Please try another method.');
          break;
        default:
          setMessage('⚠️ Something went wrong.');
          break;
      }
    }

    setIsProcessing(false);
  };

  return (
    <Container style={{ maxWidth: 600, paddingTop: 24 }}>
      <Card className="p-3">
        <h5>Pay ${(plan?.amount || 1999) / 100}</h5>
        <form onSubmit={handleSubmit}>
          <PaymentElement />
          {message && (
            <Alert
              variant={message.includes('succeeded') ? 'success' : 'danger'}
              className="mt-3"
            >
              {message}
            </Alert>
          )}
          <Button className="mt-3" type="submit" disabled={!stripe || isProcessing}>
            {isProcessing ? 'Processing…' : 'Pay now'}
          </Button>
        </form>
      </Card>
    </Container>
  );
};

export default CheckoutForm;
